/**
 * Painel de homologação do caso pelo perfil Revisor (E23.F7, ADR-0024/ADR-0068). // ux-neutrality:allow doc-comment
 *
 * Separação de funções DECLARADA (E63.F1.C05): as ações Homologar/Devolver // ux-neutrality:allow doc-comment
 * aparecem para owner|revisor, inclusive para quem elaborou o caso — o servidor // ux-neutrality:allow doc-comment
 * registra a coincidência em review_mesmo_autor_producao e a tela a declara. A // ux-neutrality:allow doc-comment
 * homologação é o segundo gate do download do laudo (review_status='approved'). // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * O aviso PRÉVIO (E63.F1.C13) vem da pré-checagem do servidor, não de comparar // ux-neutrality:allow doc-comment
 * com usuario_id: no caso do portal o criador é a gestora cliente, e quem // ux-neutrality:allow doc-comment
 * produziu o laudo ficava sem aviso antes de decidir (F-E63.F1-07). E o aviso é // ux-neutrality:allow doc-comment
 * PRÉ-CONDIÇÃO da decisão (E63.F1.C16, F-E63.F1-09): as ações só habilitam // ux-neutrality:allow doc-comment
 * depois da primeira resposta, a consulta é refeita quando a autoria pode ter // ux-neutrality:allow doc-comment
 * mudado na tela (revalidateKey) e há conferência imediatamente antes do PATCH. // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * Quando a conferência falha SEM nenhum valor conhecido (E63.F1.C18), a decisão // ux-neutrality:allow doc-comment
 * fica bloqueada com nova tentativa explícita: sem informação não há aviso a // ux-neutrality:allow doc-comment
 * dar, e prometer transparência prévia enquanto se libera a decisão às cegas // ux-neutrality:allow doc-comment
 * seria a contradição que a ADR-0068 §2.3 não admite. Se o aviso LOCAL já está // ux-neutrality:allow doc-comment
 * na tela (o criador), a falha não trava nada — a pessoa foi avisada. // ux-neutrality:allow doc-comment
 */
import { useCallback, useState } from 'react';
import { Badge } from '@/components/ui';
import { LaudoBlocoRecolhivel } from '@/components/LaudoBlocoRecolhivel';
import { ReviewActions, ReviewMeta } from '@/components/LaudoReviewPanel.parts';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import { useReviewSelfHint, type ReviewSelfHint } from '@/lib/useReviewSelfHint';
import {
  reviewCase,
  type CaseRecordData,
  type ReviewDecision,
  type ReviewStatus,
} from '@/lib/cases';

export interface LaudoReviewPanelProps {
  caseRecord: CaseRecordData;
  onReviewed: (updated: CaseRecordData) => void;
  /**
   * Bump de quem sabe que a autoria pode ter mudado (gerar/aprovar/reabrir
   * narrativa é uma das três fontes de produção da ADR-0068 §3).
   */
  revalidateKey?: number | undefined;
}

const STATUS_LABEL: Record<ReviewStatus, string> = {
  pending_review: ptBR.laudoCaso.review.statusPending,
  approved: ptBR.laudoCaso.review.statusApproved,
  rejected: ptBR.laudoCaso.review.statusRejected,
};

/**
 * Homologado é resultado e entra na rampa positiva; devolvido é resultado
 * negativo; pendente é direção e fica em aço. Extraído do componente para
 * manter a complexidade ciclomática dentro do limite do lint.
 */
function statusVariant(status: ReviewStatus): 'success' | 'danger' | 'neutral' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'danger';
  return 'neutral';
}

interface EstadoDoAviso {
  /** O aviso de mesmo autor está na tela agora. */
  mostrar: boolean;
  /** Nada conferido: nem valor do servidor, nem aviso local. Decisão bloqueada. */
  semInformacao: boolean;
}

/**
 * Traduz a pré-checagem no que a tela precisa decidir. Fora do componente para
 * manter a complexidade ciclomática dentro do limite do lint.
 */
function estadoDoAviso(canReview: boolean, hint: ReviewSelfHint): EstadoDoAviso {
  if (!canReview) return { mostrar: false, semInformacao: false };
  if (hint.mesmoAutor) return { mostrar: true, semInformacao: false };
  return { mostrar: false, semInformacao: hint.status === 'error' && !hint.doServidor };
}

export function LaudoReviewPanel({
  caseRecord,
  onReviewed,
  revalidateKey,
}: LaudoReviewPanelProps): JSX.Element {
  const client = useApiClient();
  const { role, userId } = useCurrentRole();
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState<ReviewDecision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avisoMudou, setAvisoMudou] = useState(false);
  // E65.F1.T02: fechado por default — a homologação é passo ocasional e ficava
  // ocupando altura fixa acima do trabalho de produzir o laudo.
  const [aberto, setAberto] = useState(false);

  const status = caseRecord.review_status ?? 'pending_review';
  const isCreator = userId !== null && userId === caseRecord.usuario_id;
  const canReview = role === 'owner' || role === 'revisor';
  // Quem elaborou nem sempre é quem criou (no portal o criador é a cliente):
  // o predicado prospectivo vem do servidor, das mesmas três fontes que a
  // homologação vai declarar (E63.F1.C13).
  const hint = useReviewSelfHint({
    caseId: caseRecord.id,
    enabled: canReview,
    fallback: isCreator,
    revalidateKey,
  });
  // `semInformacao`: ninguém conferiu nada — decidir aqui seria decidir às
  // cegas, e a ADR-0068 §2.3 promete o contrário. O produto escolhe o aviso em
  // vez da disponibilidade, com nova tentativa explícita (F-E63.F1-09).
  const { mostrar: mostrarAviso, semInformacao } = estadoDoAviso(canReview, hint);

  const submit = useCallback(
    async (decision: ReviewDecision) => {
      setBusy(decision);
      setError(null);
      setSuccess(null);
      setAvisoMudou(false);
      try {
        // Conferência na janela entre o clique e o envio: se a coincidência
        // apareceu depois do que a tela mostrou, a decisão volta para quem
        // decide — com o aviso agora visível (F-E63.F1-09).
        const exibido = mostrarAviso;
        const { mesmoAutor: atual, doServidor } = await hint.revalidate();
        // Fail-open só com informação: valor do servidor OU aviso local já
        // visível. Sem nenhum dos dois, a decisão não sai (F-E63.F1-09).
        if (!doServidor && !exibido) return;
        if (atual && !exibido) {
          setAvisoMudou(true);
          return;
        }
        const trimmed = notes.trim();
        const updated = await reviewCase(client, caseRecord.id, {
          decision,
          notes: trimmed === '' ? null : trimmed,
        });
        onReviewed(updated);
        setSuccess(
          decision === 'approved'
            ? ptBR.laudoCaso.review.approveSuccess
            : ptBR.laudoCaso.review.rejectSuccess,
        );
        // Decidido: o formulário recolhe e o que fica é o registro (badge de
        // estado no cabeçalho, quem homologou e a confirmação no rodapé).
        setAberto(false);
      } catch {
        setError(ptBR.laudoCaso.review.errorGeneric);
      } finally {
        setBusy(null);
      }
    },
    [client, caseRecord.id, notes, onReviewed, hint, mostrarAviso],
  );

  return (
    <LaudoBlocoRecolhivel
      testId="laudo-review-panel"
      dataAttrs={{ 'data-review-status': status }}
      kicker={ptBR.laudoCaso.review.panelKicker}
      titulo={ptBR.laudoCaso.review.panelTitle}
      aberto={aberto}
      onAbertoChange={setAberto}
      // O estado do gate fica FORA do recolhimento (E65.F1.T02): homologação
      // pendente atrás de um bloco fechado pareceria gate ausente. A tag sempre
      // carrega texto — cor nunca é a única portadora.
      acessorio={
        <Badge variant={statusVariant(status)} data-testid="laudo-review-status">
          {STATUS_LABEL[status]}
        </Badge>
      }
      // O REGISTRO da homologação não recolhe: quem homologou, quando, a
      // declaração de mesmo autor (ADR-0068) e o desfecho da última decisão são
      // o resultado do bloco, não o formulário dele.
      rodape={
        <>
          <ReviewMeta caseRecord={caseRecord} />
          {success !== null && (
            <p
              role="status"
              data-testid="laudo-review-success"
              className="mt-sc-3 border border-success bg-success-50 px-sc-3 py-2 text-body-sm text-success-700"
            >
              {success}
            </p>
          )}
          {error !== null && (
            <p
              role="alert"
              data-testid="laudo-review-error"
              className="mt-sc-3 border border-danger bg-danger-50 px-sc-3 py-2 text-body-sm text-danger-700"
            >
              {error}
            </p>
          )}
        </>
      }
    >
      {/* Aviso, não bloqueio: quem elaborou vê antes de decidir que a
          homologação sairá declarada como do próprio autor (ADR-0068). */}
      {mostrarAviso && (
        <p className="text-body-sm text-fg-muted" data-testid="laudo-review-self-hint">
          {ptBR.laudoCaso.review.selfReviewHint}
        </p>
      )}

      {canReview && (
        <ReviewActions
          notes={notes}
          busy={busy}
          aguardandoAviso={hint.status === 'loading'}
          semInformacao={semInformacao}
          onNotesChange={setNotes}
          onSubmit={submit}
          onRetryAviso={hint.retry}
        />
      )}

      {avisoMudou && (
        <p
          role="alert"
          data-testid="laudo-review-hint-changed"
          className="mt-sc-3 border border-border bg-surface-2 px-sc-3 py-2 text-body-sm text-fg"
        >
          {ptBR.laudoCaso.review.selfReviewChanged}
        </p>
      )}
    </LaudoBlocoRecolhivel>
  );
}
